import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from 'react'
import type { User, AuthError } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import type { Database } from '../types/database'

import { logger } from '@/lib/logger'
// User profile type from our database
type UserProfile = Database['public']['Tables']['users']['Row']

// Session type from Supabase
type Session = Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session']

interface AuthContextType {
  // Auth state
  user: User | null
  profile: UserProfile | null
  session: Session
  loading: boolean

  // Auth methods
  signUp: (email: string, password: string, displayName: string, role: 'facilitator' | 'participant') => Promise<{ error: AuthError | null }>
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>
  signInWithGoogle: () => Promise<{ error: AuthError | null }>
  signInWithToken: (token: string) => Promise<{ error: Error | null }>
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>
  updatePassword: (password: string) => Promise<{ error: AuthError | null }>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
  grantConsent: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(true)

  // Combined loading: true until BOTH auth session AND profile are resolved
  const loading = authLoading || profileLoading

  // PERFORMANCE: Track pending/completed fetches to prevent duplicate requests
  const profileFetchRef = useRef<{ userId: string | null; inProgress: boolean }>({ userId: null, inProgress: false })
  const profileCacheRef = useRef<Map<string, UserProfile>>(new Map())

  // Fetch user profile from our users table
  // PERFORMANCE: Uses caching and deduplication to prevent redundant fetches
  const fetchProfile = async (userId: string, forceRefresh = false): Promise<UserProfile | null> => {
    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = profileCacheRef.current.get(userId)
      if (cached) {
        logger.log('🔍 fetchProfile: Returning cached profile for:', userId)
        return cached
      }
    }

    // Prevent duplicate in-flight requests
    if (profileFetchRef.current.userId === userId && profileFetchRef.current.inProgress) {
      logger.log('🔍 fetchProfile: Skipping duplicate request for:', userId)
      // Wait a bit and check cache
      await new Promise(resolve => setTimeout(resolve, 100))
      return profileCacheRef.current.get(userId) || null
    }

    profileFetchRef.current = { userId, inProgress: true }
    logger.log('🔍 fetchProfile: Starting for userId:', userId)

    try {
      // Reduced timeout to 5s (was 10s) - if it takes longer, something is wrong
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Profile query timeout after 5s')), 5000)
      })

      const queryPromise = supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle()

      logger.log('🔍 fetchProfile: Query created, waiting...')
      const startTime = Date.now()

      const { data, error } = await Promise.race([queryPromise, timeoutPromise]) as any

      const elapsed = Date.now() - startTime
      logger.log(`🔍 fetchProfile: Query returned in ${elapsed}ms`, { hasData: !!data, error: error?.message })

      if (error) {
        logger.error('Error fetching profile:', error)
        return null
      }

      // If profile doesn't exist, create it from auth metadata
      if (!data) {
        logger.log('Profile not found, creating from auth metadata...')
        const { data: { user } } = await supabase.auth.getUser()

        if (user?.user_metadata) {
          // Use upsert to avoid duplicate key errors
          const { data: newProfile, error: createError } = await supabase
            .from('users')
            .upsert({
              id: userId,
              email: user.email!,
              display_name: user.user_metadata.display_name || user.email?.split('@')[0] || 'User',
              role: user.user_metadata.role || 'participant',
              status: 'registered',
            }, {
              onConflict: 'id'
            })
            .select()
            .single()

          if (createError) {
            logger.error('Error creating profile:', createError)
            return null
          }

          // Cache the new profile
          if (newProfile) {
            profileCacheRef.current.set(userId, newProfile)
          }
          return newProfile
        }
      }

      // Cache the fetched profile
      if (data) {
        profileCacheRef.current.set(userId, data)
      }

      return data
    } catch (err: any) {
      logger.error('❌ fetchProfile exception:', err.message)
      return null
    } finally {
      profileFetchRef.current = { userId: null, inProgress: false }
    }
  }

  // MULTI-DEVICE: Refresh profile when window regains focus (in case updated elsewhere)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        // Clear cache and refetch - but don't block UI
        profileCacheRef.current.clear()
        fetchProfile(user.id).then(profile => {
          if (profile) setProfile(profile)
        })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [user])

  // ──────────────────────────────────────────────────────────────────────────
  // AUTH INITIALIZATION — Two-effect pattern (community best practice)
  //
  // Why two effects?
  // 1. Supabase has a known deadlock bug: making async Supabase API calls
  //    inside onAuthStateChange causes the client to hang (auth-js #762,
  //    supabase-js #2013). The profile fetch queries the `users` table,
  //    which is a Supabase API call — so it CANNOT go inside the listener.
  //
  // 2. INITIAL_SESSION fires once (on subscription setup), not after login.
  //    SIGNED_IN fires after login but we can't fetch profile inside it.
  //    Solution: listener only sets session/user state (sync), and a
  //    separate reactive effect fetches profile when user?.id changes.
  //
  // Flow:
  //   Page load → INITIAL_SESSION → authLoading=false, user set from storage
  //   Fresh login → SIGNED_IN → user set → profile effect triggers
  //   Sign out → SIGNED_OUT → user=null → profile cleared
  //   Token refresh → TOKEN_REFRESHED → same user?.id → no re-fetch
  // ──────────────────────────────────────────────────────────────────────────

  // Effect 1: Auth event listener — sync state updates ONLY, no async Supabase calls
  useEffect(() => {
    let isMounted = true

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return
      logger.log('🔐 Auth state changed:', event, session?.user?.email)

      // Sync state updates only — never await supabase.* here (deadlock risk)
      setSession(session)
      setUser(session?.user ?? null)

      if (event === 'INITIAL_SESSION') {
        // Auth check complete (session recovered from storage, or null)
        setAuthLoading(false)
      }

      if (event === 'SIGNED_OUT') {
        setProfile(null)
        setProfileLoading(false)
        profileCacheRef.current.clear()
      }
    })

    return () => {
      isMounted = false
      subscription.unsubscribe()
    }
  }, [])

  // Effect 2: Reactive profile fetch — fires when user changes (login, logout, page load)
  useEffect(() => {
    // Wait for auth to resolve before attempting profile fetch
    if (authLoading) return

    // No user → clear profile
    if (!user) {
      setProfile(null)
      setProfileLoading(false)
      return
    }

    let cancelled = false
    setProfileLoading(true)
    logger.log('🔐 [ProfileEffect] Fetching profile for:', user.id)

    fetchProfile(user.id).then(profileData => {
      if (cancelled) return
      if (profileData) {
        logger.log('🔐 [ProfileEffect] Profile loaded:', profileData.role)
        setProfile(profileData)
      } else {
        logger.warn('🔐 [ProfileEffect] Profile fetch returned null for:', user.id)
      }
      setProfileLoading(false)
    }).catch(err => {
      if (cancelled) return
      logger.error('🔐 [ProfileEffect] Profile fetch failed:', err)
      setProfileLoading(false)
    })

    return () => { cancelled = true }
  }, [user?.id, authLoading])

  // Sign up with email/password
  const signUp = async (
    email: string,
    password: string,
    displayName: string,
    role: 'facilitator' | 'participant'
  ) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
        data: {
          display_name: displayName,
          role: role,
        }
      }
    })

    if (error) return { error }
    if (!data.user) return { error: new AuthError('User creation failed') as AuthError }

    // Persist profile fields + consent on the users row.
    // The auth trigger owns row CREATION; RLS blocks participants from
    // INSERT, so both a plain insert and an upsert fail silently — that is
    // why consent_given_at never stuck and the consent gate asked a second
    // time (2026-09-03). UPDATE of the own row is allowed (the same op
    // grantConsent uses successfully). Retry briefly in case the trigger's
    // row has not landed yet.
    const profileFields = {
      display_name: displayName,
      role,
      status: 'registered',
      data_consent: true,
      consent_given_at: new Date().toISOString(),
    }
    let profileUpdated = false
    for (let attempt = 0; attempt < 3 && !profileUpdated; attempt++) {
      if (attempt > 0) await new Promise(r => setTimeout(r, 700))
      const { data: rows, error: profileError } = await supabase
        .from('users')
        .update(profileFields)
        .eq('id', data.user.id)
        .select('id')
      if (profileError) {
        logger.error('Error updating profile after signup:', profileError)
        break
      }
      profileUpdated = !!rows?.length
    }
    if (!profileUpdated) {
      logger.error('Profile row not updated after signup — consent gate will re-ask')
    }

    // Refresh the in-memory profile: the auth listener may have fetched it
    // before the consent write above, and the gate reads the cached copy
    const freshProfile = await fetchProfile(data.user.id, true)
    setProfile(freshProfile)

    return { error: null }
  }

  // Sign in with Google OAuth
  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    })
    return { error }
  }

  // Sign in with email/password
  const signIn = async (email: string, password: string) => {
    logger.log('🔐 Attempting sign in for:', email)

    try {
      // Add timeout wrapper
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error('Authentication timeout after 10 seconds'))
        }, 10000)
      })

      logger.log('🔐 Calling supabase.auth.signInWithPassword...')

      const authPromise = supabase.auth.signInWithPassword({
        email,
        password,
      })

      const { error, data } = await Promise.race([authPromise, timeoutPromise]) as any

      logger.log('🔐 Sign in response:', { error: error?.message, hasSession: !!data?.session })

      if (!error && data?.session) {
        logger.log('✅ Sign in successful, skipping last_login_at update to avoid RLS issues')
        // Skip last_login_at update for now - it might be causing issues
      }

      return { error }
    } catch (err) {
      logger.error('❌ Sign in exception:', err)
      return { error: err as AuthError }
    }
  }

  // Sign in with access token (QR code flow)
  const signInWithToken = async (token: string) => {
    try {
      // Validate token and get user_id
      const { data: tokenData, error: tokenError } = await supabase
        .from('access_tokens')
        .select('user_id, expires_at, used_at, is_valid')
        .eq('token', token)
        .single()

      if (tokenError || !tokenData) {
        return { error: new Error('Invalid access token') }
      }

      // Check if token is valid
      if (!tokenData.is_valid) {
        return { error: new Error('Token has been revoked') }
      }

      if (tokenData.used_at) {
        return { error: new Error('Token has already been used') }
      }

      if (new Date(tokenData.expires_at) < new Date()) {
        return { error: new Error('Token has expired') }
      }

      // Mark token as used
      await supabase
        .from('access_tokens')
        .update({
          used_at: new Date().toISOString(),
          used_from_ip: 'web-client', // In production, get actual IP
        })
        .eq('token', token)

      // Get user email to sign in
      const { data: userData, error: userError} = await supabase
        .from('users')
        .select('email')
        .eq('id', tokenData.user_id)
        .single()

      if (userError || !userData) {
        return { error: new Error('User not found') }
      }

      // For QR code login, we need to use Supabase's magic link or create a session
      // Since we can't directly create a session, we'll use a temporary password approach
      // In production, you might want to use Supabase's admin API to create a session

      // For now, return success and set the user manually
      // This is a simplified version - in production, use proper session creation
      return { error: new Error('QR code login requires server-side session creation') }

    } catch (err) {
      return { error: err instanceof Error ? err : new Error('Unknown error') }
    }
  }

  // Reset password (send email)
  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/update-password`,
    })
    return { error }
  }

  // Update password (after reset link clicked)
  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password })
    return { error }
  }

  // Sign out
  const signOut = async () => {
    await supabase.auth.signOut()
    setUser(null)
    setProfile(null)
    setSession(null)
  }

  // Grant GDPR consent — updates DB and refreshes profile
  const grantConsent = async () => {
    if (!user) return

    const { error } = await supabase
      .from('users')
      .update({
        data_consent: true,
        consent_given_at: new Date().toISOString(),
      })
      .eq('id', user.id)

    if (error) {
      logger.error('Error granting consent:', error)
      throw error
    }

    // Refresh profile so ProtectedRoute re-evaluates
    await refreshProfile()
  }

  // Refresh profile (useful after updates)
  const refreshProfile = async () => {
    if (user) {
      const profile = await fetchProfile(user.id, true) // Force refresh, bypass cache
      setProfile(profile)
    }
  }

  const value = {
    user,
    profile,
    session,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithToken,
    resetPassword,
    updatePassword,
    signOut,
    refreshProfile,
    grantConsent,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
